/**
 * Cloudinary Image Upload Service
 * 
 * Simple, reliable image upload service using Cloudinary's unsigned upload.
 * No complex authentication or session management required.
 */

interface CloudinaryUploadResponse {
  secure_url: string;
  public_id: string;
  width: number;
  height: number;
  format: string;
  resource_type: string;
  created_at: string;
  bytes: number;
}

interface UploadResult {
  data?: {
    publicUrl: string;
    publicId: string;
    width: number;
    height: number;
    format: string;
    size: number;
  };
  error?: Error;
}

class CloudinaryService {
  private cloudName: string;
  private uploadPreset: string;
  private apiUrl: string;

  constructor() {
    this.cloudName = process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME || '';
    this.uploadPreset = process.env.NEXT_PUBLIC_CLOUDINARY_UPLOAD_PRESET || '';
    this.apiUrl = `https://api.cloudinary.com/v1_1/${this.cloudName}/image/upload`;

    if (!this.cloudName || !this.uploadPreset) {
      console.warn('⚠️ Cloudinary configuration missing. Please set NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME and NEXT_PUBLIC_CLOUDINARY_UPLOAD_PRESET environment variables.');
    }
  }

  /**
   * Upload image to Cloudinary
   * @param file - Image file to upload
   * @param folder - Optional folder name for organization (e.g., 'articles/featured', 'articles/content')
   * @returns Upload result with public URL and metadata
   */
  async uploadImage(file: File, folder?: string): Promise<UploadResult> {
    try {
      // Validate configuration
      if (!this.cloudName || !this.uploadPreset) {
        throw new Error(
          'Cloudinary is not configured. Please follow the setup guide at CLOUDINARY_SETUP_GUIDE.md to configure environment variables.'
        );
      }

      // Validate file
      if (!file) {
        throw new Error('No file provided');
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Invalid file type. Only JPEG, PNG, WebP, and GIF images are allowed.');
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        throw new Error('File size exceeds 10MB limit');
      }

      console.log('📤 Starting Cloudinary upload:', {
        fileName: file.name,
        fileSize: `${(file.size / 1024).toFixed(2)}KB`,
        fileType: file.type,
        folder: folder || 'default',
        cloudName: this.cloudName,
        uploadPreset: this.uploadPreset
      });

      // Prepare form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', this.uploadPreset);
      
      // Add folder if specified
      if (folder) {
        formData.append('folder', folder);
      }

      // Add automatic optimization settings
      formData.append('quality', 'auto:good');
      formData.append('fetch_format', 'auto');

      // Upload to Cloudinary
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Cloudinary upload failed:', errorData);
        
        // Enhanced error message for "Upload preset not found"
        if (errorData.error?.message === 'Upload preset not found') {
          throw new Error(
            `Upload preset "${this.uploadPreset}" not found in your Cloudinary account.\n\n` +
            `SOLUTION:\n` +
            `1. Go to your Cloudinary dashboard: https://cloudinary.com/console\n` +
            `2. Navigate to Settings → Upload tab\n` +
            `3. Click "Add upload preset"\n` +
            `4. Set preset name to: ${this.uploadPreset}\n` +
            `5. Set "Signing Mode" to "Unsigned"\n` +
            `6. Click Save\n\n` +
            `For detailed instructions, see CLOUDINARY_SETUP_GUIDE.md`
          );
        }
        
        throw new Error(errorData.error?.message || 'Upload failed');
      }

      const data: CloudinaryUploadResponse = await response.json();

      console.log('✅ Cloudinary upload successful:', {
        url: data.secure_url,
        publicId: data.public_id,
        size: `${(data.bytes / 1024).toFixed(2)}KB`
      });

      return {
        data: {
          publicUrl: data.secure_url,
          publicId: data.public_id,
          width: data.width,
          height: data.height,
          format: data.format,
          size: data.bytes
        }
      };

    } catch (error) {
      console.error('❌ Error uploading to Cloudinary:', error);
      
      return {
        error: error instanceof Error 
          ? error 
          : new Error('Failed to upload image to Cloudinary')
      };
    }
  }

  /**
   * Upload featured image for articles
   * Automatically organizes into 'articles/featured' folder
   */
  async uploadFeaturedImage(file: File): Promise<UploadResult> {
    return this.uploadImage(file, 'articles/featured');
  }

  /**
   * Upload content image for articles
   * Automatically organizes into 'articles/content' folder
   */
  async uploadContentImage(file: File): Promise<UploadResult> {
    try {
      if (!this.cloudName || !this.uploadPreset) {
        throw new Error('Cloudinary not configured. Please check CLOUDINARY_SETUP_GUIDE.md');
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', this.uploadPreset);
      formData.append('folder', 'articles/content');

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${this.cloudName}/image/upload`,
        {
          method: 'POST',
          body: formData
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Upload failed');
      }

      const data = await response.json();

      return {
        data: {
          publicUrl: data.secure_url,
          publicId: data.public_id,
          width: data.width,
          height: data.height,
          format: data.format,
          size: data.bytes
        }
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error : new Error('Unknown upload error')
      };
    }
  }

  /**
   * Delete image from Cloudinary
   * @param publicId - Public ID of the image to delete
   */
  async deleteImage(publicId: string): Promise<{ success: boolean; error?: Error }> {
    try {
      // Note: Deletion requires authenticated requests, which need backend implementation
      // For now, we'll log a warning and return success
      console.warn('⚠️ Image deletion requires backend implementation with Cloudinary API credentials');
      console.log('Image to delete:', publicId);
      
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting from Cloudinary:', error);
      return {
        success: false,
        error: error instanceof Error 
          ? error 
          : new Error('Failed to delete image')
      };
    }
  }

  /**
   * Generate optimized image URL with transformations
   * @param publicUrl - Original Cloudinary URL
   * @param options - Transformation options
   */
  getOptimizedUrl(
    publicUrl: string,
    options: {
      width?: number;
      height?: number;
      crop?: 'fill' | 'fit' | 'scale' | 'limit' | 'pad';
      quality?: 'auto' | 'auto:best' | 'auto:good' | 'auto:eco' | 'auto:low' | number;
      format?: 'auto' | 'webp' | 'jpg' | 'png' | 'avif';
      dpr?: 'auto' | number;
      aspectRatio?: string;
    } = {}
  ): string {
    try {
      // Parse the Cloudinary URL
      const urlParts = publicUrl.split('/upload/');
      if (urlParts.length !== 2) {
        return publicUrl; // Return original if not a valid Cloudinary URL
      }

      // Build transformation string
      const transformations: string[] = [];
      
      if (options.width) transformations.push(`w_${options.width}`);
      if (options.height) transformations.push(`h_${options.height}`);
      if (options.crop) transformations.push(`c_${options.crop}`);
      if (options.quality) transformations.push(`q_${options.quality}`);
      if (options.format) transformations.push(`f_${options.format}`);
      if (options.dpr) transformations.push(`dpr_${options.dpr}`);
      if (options.aspectRatio) transformations.push(`ar_${options.aspectRatio}`);

      // Add default optimizations if none specified
      if (transformations.length === 0) {
        transformations.push('q_auto', 'f_auto');
      }

      // Construct optimized URL
      return `${urlParts[0]}/upload/${transformations.join(',')}/${urlParts[1]}`;
    } catch (error) {
      console.error('Error generating optimized URL:', error);
      return publicUrl; // Return original on error
    }
  }

  /**
   * Generate responsive srcset for Cloudinary images
   * @param publicUrl - Original Cloudinary URL
   * @param widths - Array of widths to generate
   * @returns srcset string for responsive images
   */
  getResponsiveSrcSet(
    publicUrl: string,
    widths: number[] = [320, 640, 768, 1024, 1280, 1536]
  ): string {
    try {
      return widths
        .map(width => {
          const optimizedUrl = this.getOptimizedUrl(publicUrl, {
            width,
            quality: 'auto:good',
            format: 'auto',
            dpr: 'auto'
          });
          return `${optimizedUrl} ${width}w`;
        })
        .join(', ');
    } catch (error) {
      console.error('Error generating responsive srcset:', error);
      return '';
    }
  }

  /**
   * Generate optimized thumbnail URL
   * @param publicUrl - Original Cloudinary URL
   * @param size - Thumbnail size (default: 200)
   */
  getThumbnailUrl(publicUrl: string, size: number = 200): string {
    return this.getOptimizedUrl(publicUrl, {
      width: size,
      height: size,
      crop: 'fill',
      quality: 'auto:good',
      format: 'auto'
    });
  }

  /**
   * Generate optimized featured image URL for articles
   * @param publicUrl - Original Cloudinary URL
   */
  getFeaturedImageUrl(publicUrl: string): string {
    return this.getOptimizedUrl(publicUrl, {
      width: 1200,
      height: 630,
      crop: 'fill',
      quality: 'auto:best',
      format: 'auto',
      dpr: 'auto'
    });
  }

  /**
   * Generate optimized card image URL
   * @param publicUrl - Original Cloudinary URL
   */
  getCardImageUrl(publicUrl: string): string {
    return this.getOptimizedUrl(publicUrl, {
      width: 600,
      height: 400,
      crop: 'fill',
      quality: 'auto:good',
      format: 'auto',
      dpr: 'auto'
    });
  }

  /**
   * Generate thumbnail URL for media grid display
   */
  private generateThumbnailUrl(publicId: string, width: number, height: number): string {
    return `https://res.cloudinary.com/${this.cloudName}/image/upload/c_fill,w_${width},h_${height}/${publicId}`;
  }
}

// Export singleton instance
export const cloudinaryService = new CloudinaryService();